/**
 * Client → same-origin POST /api/interpret (Phase 2b).
 * Never calls OpenRouter; API secrets stay on the server only.
 */


/**
 * Client abort budget (spec §15).
 * Production runs on Cloudflare Pages Functions, so leave generous margin for
 * network + JSON parse on top of the server response.
 */
export const INTERPRET_TIMEOUT_MS = 45000;

/**
 * POST an InterpretationPayload and return the rendered markdown reading.
 * The server returns a non-streaming HTTP 200 JSON body `{ markdown }`; we
 * await res.json() and return data.markdown so the UI can render the full
 * reading at once (the backend returns a non-streaming response by design).
 *
 * @param {object} payload - InterpretationPayload
 * @param {{ email: string, code: string, type?: 'tester' | 'access' }} auth
 * @param {{ signal?: AbortSignal, lifeIntensity?: string }} [options]
 * @returns {Promise<string>} the full markdown text
 */
export async function fetchLlmReading(payload, auth, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), INTERPRET_TIMEOUT_MS);

  const onOuterAbort = () => controller.abort();
  if (options.signal) {
    if (options.signal.aborted) controller.abort();
    else options.signal.addEventListener("abort", onOuterAbort, { once: true });
  }

  try {
    const res = await fetch("/api/interpret", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
      body: JSON.stringify({
        auth: {
          type: auth.type || "tester",
          email: auth.email,
          code: auth.code,
        },
        payload,
        lifeIntensity: options.lifeIntensity || "dramatic",
      }),
    });

    let data = null;
    try {
      data = await res.json();
    } catch {
      data = null;
    }

    if (!res.ok || !data) {
      const msg =
        (data && data.message) ||
        defaultErrorMessage(res.status, data && data.error);
      const err = new Error(msg);
      err.status = res.status;
      err.code = (data && data.error) || "http_error";
      throw err;
    }

    const markdown = data.markdown;
    if (typeof markdown !== "string" || markdown.length === 0) {
      const err = new Error(
        "The reading service returned an unusable response. Please try again."
      );
      err.status = 502;
      err.code = "upstream_empty";
      throw err;
    }
    return markdown;
  } catch (err) {
    if (err && (err.name === "AbortError" || err.code === "ABORT_ERR")) {
      const e = new Error(
        "The reading took too long (over 45 seconds). Your previous content was kept."
      );
      e.code = "timeout";
      throw e;
    }
    throw err;
  } finally {
    clearTimeout(timer);
    if (options.signal) {
      options.signal.removeEventListener("abort", onOuterAbort);
    }
  }
}

function defaultErrorMessage(status, code) {
  switch (status) {
    case 401:
      return "Tester authentication failed. Unlock again with email and invite code.";
    case 402:
      return "Payment or credits required.";
    case 429:
      return "Too many requests. Please wait and try again.";
    case 400:
      return "The chart payload was invalid.";
    case 502:
      return "The AI reading service returned an unusable response. Please try again.";
    case 503:
      return "AI readings are not configured on the server yet.";
    case 504:
      return "The reading service timed out. Please try again.";
    default:
      return code
        ? `Reading request failed (${code}).`
        : `Reading request failed (HTTP ${status}).`;
  }
}
